<script lang="ts" setup>
	import { useSeoSchema } from '~/utils/seo-schema'
	import type { H1Config } from '~/components/ui/h1.vue'
	import type { FAQContext } from '~/components/main/faq.vue'
	import type { usedHospitalContext } from '~/components/main/most-used-hospitals.vue'
	import type { teamBenefitsContext } from '~/components/main/productivity-benefits.vue'
	import type { BenefitHighlightContext } from '~/components/molecules/benefit-highlight.vue'
	import type { ProcessesStepContext } from '~/components/molecules/process-steps.vue'
	import { useMediaQuery } from '@vueuse/core'

	definePageMeta({
		layout: 'default'
	})
	const seo = {
		title: 'MyCoverGenius | PrimeCare | Premium HMO for businesses',
		description: "Boost your team's productivity with premium health insurance designed  for businesses",
		tags: ['Health insurance'],
		url: `${import.meta.env.VITE_BUSINESS_URL}/primecare`
	}
	useSeoMeta({
		title: seo.title,
		description: seo.description,
		ogTitle: seo.title,
		ogDescription: seo.description,
		ogType: 'website',
		ogLocale: 'en_NG',
		articleTag: seo.tags,
		ogUrl: seo.url,
		twitterTitle: seo.title,
		twitterDescription: seo.description,
		twitterSite: '@mycovergenius',
		twitterCard: 'summary_large_image',
		twitterAppUrlGoogleplay: seo.url,
		twitterAppUrlIphone: seo.url,
		robots: {
			all: 'true',
			follow: 'true',
			index: 'true'
		}
	})
	const isDialogOpen = shallowRef(false)
	const showBenefits = shallowRef(false)
	const focusMode = shallowRef(false)
	const isMobile = useMediaQuery('(max-width: 767px)')
	const { navigateToProduct, viewMoreProducts } = useProductsStore()

	const tabsCategory = computed<Record<'staffList' | 'businessManager', BenefitHighlightContext>>(() => ({
		staffList: [
			{
				title: 'Top-tier hospital',
				description: 'With PrimeCare, you get access to a network of over 2,000 premium healthcare centres.',
				image: '/v1727382416/nationwide-coverage_d2xqoc.webp',
				open: true
			},
			{
				title: '24/7 Hospital support',
				description:
					'Count on us to provide immediate support to your staff; for general hospital inquiries or emergencies, anytime, day or night.',
				image: '/v1727382600/need-help_x5u2bd.webp'
			},
			{
				title: 'Telemedicine',
				description:
					'Your staff can easily connect with healthcare professionals from anywhere with their prescriptions delivered.',
				image: '/v1727082909/telemedicine_cg7h69.webp'
			},
			{
				title: 'International Reimbursement',
				description: 'Access medical services worldwide and get reimbursed for expenses incurred abroad.',
				image: '/v1727382276/internation-reimbursement_fsc66x.webp'
			},
			{
				title: 'Employee Mobile App',
				description:
					"Whether they're at work or on the move, your team can manage their plan, find a hospital, or talk to a doctor without delays all from the MyCover app.",
				image: '/v1761841544/mobile-app-comms_r9lqsn.webp'
			}
		],
		businessManager: [
			{
				title: 'All-in-one business dashboard',
				description: 'With our platform, you can easily manage your plans, staff and claims in real time.',
				image: '/v1727082976/business-tag_qjjwhs.webp',
				open: true
			},
			{
				title: 'Dedicated account manager',
				description: 'Get personalised support and assistance from your dedicated account manager.',
				image: '/v1727082969/account-manager_dcxssg.webp',
				open: false
			},
			{
				title: 'Usage History',
				description:
					'View transaction history from your account dashboard, including premium payments, employee utilization, and renewals.',
				image: '/v1727082972/usage-history_wkmtou.webp',
				open: false
			},
			{
				title: 'Batch operations',
				description:
					"Our batch processing feature enables you to upload your entire team's information in one go, saving you time and effort.",
				image: '/v1727082964/batch-operation_m2hjlz.webp',
				open: false
			},
			{
				title: 'Delegate like a genius',
				description: 'Assign roles and permissions to trusted teammates. Share the control, not the stress.',
				image: '/v1727082974/delegate_z0upkv.webp',
				open: false
			},
			{
				title: 'Flexible plans and staff management',
				description: 'Add and remove staff when they join or switch to a new plan with a few clicks.',
				image: '/v1727082958/flexible-plans_bbniu4.webp',
				open: false
			}
		]
	}))
	const faqList = ref<FAQContext[]>([
		{
			question: 'When will my policy become active after I activate it?',
			answer: 'Your H.M.O policy will become active 24 hours after activation. You can start accessing care once the activation period is complete.',
			open: true
		},
		{
			question: 'How do I access the list of hospitals on PrimeCare?',
			answer: "You can see the hospital list directly on the PrimeCare page or access it from your dashboard once you're logged in. This will provide you with a comprehensive list of all the hospitals where you can receive care under your plan.",
			open: false
		},
		{
			question: 'What is the least number of staff to add on PrimeCare?',
			answer: 'As a business, the minimum number of staff you can add on the PrimeCare plan is 2.',
			open: false
		},
		{
			question: 'Can I remove a staff from my plan when he/she leaves the company?',
			answer: 'Yes, you can easily remove staff members who no longer work at your company, either manually or in batches. Your dashboard enables this to help simplify plan and employee management.',
			open: false
		},
		{
			question: 'Is there a penalty for late renewal?',
			answer: 'If you renew within <span class="font-semibold">7 days after expiry</span>, your plan will reactivate within 24 hours. If you renew after <span class="font-semibold">7 days of expiry</span>, your plan will have a <span class="font-semibold">7-day waiting period</span> before it becomes active.',
			open: false
		}
	])

	const { generateProductSchema, generateFaqSchema, generateBreadcrumbSchema } = useSeoSchema()

	useHead({
		script: [
			{
				type: 'application/ld+json',
				innerHTML: JSON.stringify(
					generateProductSchema({
						name: 'PrimeCare',
						description: seo.description,
						image: 'https://res.cloudinary.com/dzhhy3ovo/image/upload/v1727082914/primcare-hero_sxkhzl.webp',
						url: seo.url,
						price: '0'
					})
				)
			},
			{ type: 'application/ld+json', innerHTML: JSON.stringify(generateFaqSchema(faqList.value)) },
			{
				type: 'application/ld+json',
				innerHTML: JSON.stringify(
					generateBreadcrumbSchema([
						{ name: 'Home', item: 'https://mycovergenius.com/' },
						{ name: 'PrimeCare', item: seo.url }
					])
				)
			}
		],
		link: [{ rel: 'canonical', href: seo.url }]
	})

	const onboardingProcesses = shallowRef<ProcessesStepContext>([
		{
			icon: {
				dir: 'random',
				icon: 'shopping-cart',
				height: '80',
				width: '78'
			},
			title: 'Choose your plan',
			description: 'Select any PrimeCare plan that best fits your business needs'
		},
		{
			icon: {
				dir: 'random',
				icon: 'wishlist-blue',
				height: '80',
				width: '60'
			},
			title: 'Onboard your staff',
			description: 'Onboard your staff through our simple batch operations'
		},
		{
			icon: {
				dir: 'random',
				icon: 'umbrella-2',
				height: '80',
				width: '80'
			},
			title: 'Access healthcare',
			description:
				'Staff can now access care at their preferred hospital with their digital HMO ID. It’s that simple'
		}
	])

	const mostlyUsedHospitals: usedHospitalContext[] = [
		{
			name: {
				text: 'Marcelle Ruth '
			},
			location: {
				text: 'Plot 1192D Olubosi Cl, Victoria Island, Lagos'
			}
		},
		{
			name: {
				text: 'Reddington Lekki Hospital'
			},
			location: {
				text: '15c  Admiralty Way Lekki Phase 1, Lagos'
			}
		},
		{
			name: {
				text: 'EverCare hospital'
			},
			location: {
				text: '1, Admiralty Way, Lekki Phase 1, Lagos'
			}
		},
		{
			name: {
				text: 'Reddington V.I Hospital'
			},
			location: {
				text: '12 Idowu Martins Street, Victoria Island, Lagos'
			}
		},
		{
			name: {
				text: 'Coptic Hospital'
			},
			location: {
				text: 'Plot 21, Block 11, Abila Road, Oniru Private Estate, Victoria Island'
			}
		},
		{
			name: {
				text: 'Lagoon Hospital, Ikeja'
			},
			location: {
				text: '91 Adeniyi Jones, Ogba, Ojodu 101233, Lagos'
			}
		},
		{
			name: {
				text: 'Lagoon Hospital, Ikoyi'
			},
			location: {
				text: '17B Bourdillon Rd, Ikoyi, Lagos 106104, Lagos'
			}
		}
	]
	const btnClass: H1Config = {
		wrapperClass: {
			alignItems: '',
			overflow: ''
		},
		hClass: {
			wrapper: {
				alignItems: 'items-start'
			}
		},
		paragraphClass: {
			maxWidth: 'max-w-[634px] md:leading-[32px]',
			text: 'md:text-xl'
		},
		btn: {
			base: 'md:mt-20',
			space: '',
			iconWrapper: {
				base: ' flex-col inline-flex'
			},
			primary: {
				btn: {
					base: 'bg-white  text-base text-health-800 hover:text-white hover:bg-health-500',
					onHoverPseudo: 'hover:before:bg-health-500',
					width: ''
				},
				text: { size: '' }
			},
			secondary: {
				base: 'default-btn text-center justify-center gap-x-3',
				text: 'text-sm md:text-base'
			}
		}
	}
	const teamBenefits: teamBenefitsContext = [
		{
			title: 'Local and international <br /> coverage',
			description: 'PrimeCare provides reimbursement for international care.',
			icon: {
				icon: 'glober',
				dir: 'random',
				height: '60',
				width: '60'
			}
		},
		{
			title: 'Treatment of chronic <br /> conditions',
			description:
				'Expert care for chronic conditions including specialist consultations, laboratory tests, and chronic disease medication.',
			icon: {
				icon: 'radiation',
				dir: 'random',
				height: '64',
				width: '64'
			}
		},
		{
			title: 'Cancer <br /> care',
			description: 'Provide coverage for your team against critical illnesses such as cancer care.',
			icon: {
				icon: 'purple-ribbon',
				dir: 'random',
				height: '60',
				width: '60'
			}
		},
		{
			title: 'Fertility <br /> Services',
			description:
				'Provide your team with access to comprehensive fertility services, ensuring they have the support and resources they need for reproductive health.',
			icon: {
				icon: 'cart',
				dir: 'random',
				height: '60',
				width: '60'
			}
		},
		{
			title: 'Intensive care <br /> unit',
			description:
				'Offer your team personalized care and 24/7 support, including access to the Intensive Care Unit when the need arises.',
			icon: {
				icon: 'health-pack',
				dir: 'random',
				height: '60',
				width: '60'
			}
		},
		{
			title: 'Flexible gym <br /> access',
			description: "Invest in your team's well-being and watch productivity soar with our flexible gym access.",
			icon: {
				icon: 'gym-handle',
				dir: 'random',
				height: '60',
				width: '60'
			}
		}
	]
</script>

<template>
	<div>
		<header class="md:min-h-[770px] mt-0 bg-health-800 overflow-hidden sm:px-5 md:px-0 pt-12 md:pt-24">
			<ui-pad :ui="{ paddingBlock: '', paddingInline: 'md:px-10 xs:px-5' }" class="w-full">
				<div class="flex items-center justify-between w-full lg:flex-nowrap flex-wrap gap-y-10">
					<ui-h1
						tag="PrimeCare"
						:tag-icon="{ dir: 'fill', icon: 'heart-rate-pulse' }"
						paragraph="Experience a premium lifestyle at its best."
						:ui="btnClass"
						:primary-btn-text="`Enroll your staff in 5 minutes ${isMobile ? '' : 'or less'}`"
						secondary-btn-text="Speak to us to get on board"
						:action="{ type: 'onboard', product: 'primecare' }"
					>
						<template #title="{ highlightColor }">
							<span
								class="block max-sm:max-w-[360px] text-[2.75rem]/[3.135rem] -tracking-[0.0825rem] md:text-[3.625rem]/[3.83rem] md:-tracking-[0.10rem] lg:text-[4.25rem]/[4.5rem] lg:-tracking-[0.135rem]"
							>
								More than health insurance; This is <br />
								<span :class="highlightColor">PrimeCare</span>
							</span>
						</template>
					</ui-h1>
					<div class="flex-none max-sm:ml-auto">
						<animated-prime-care
							alt="Executive holding PrimeCare premium insurance card"
							src="/v1727082914/primcare-hero_sxkhzl.webp"
							class="lg:translate-y-5"
							:ui="{
								img: { height: 'md:max-h-[656px] max-h-[341px] sm:max-h-[400px]' }
							}"
						/>
					</div>
				</div>
			</ui-pad>
		</header>
		<ui-spacer />
		<ui-pad :ui="{ paddingBlock: '' }">
			<molecules-partners />
			<ui-spacer />

			<main-health-care-experience
				title="Quality & affordable healthcare at <br /> your fingertips!"
				description="Our goal is to provide your team with the best possible tools and support, ensuring their health and well-being are our top priorities."
				:staff-list="tabsCategory.staffList"
			/>
			<ui-spacer />
			<main-productivity-benefits
				title="Benefits made for your teams healthy lifestyle"
				description="It's more than just health insurance; it's about providing benefits that resonate with your team's lifestyle."
				:team-benefits="teamBenefits"
				product-name="PrimeCare"
				@trigger="() => [(focusMode = true), (showBenefits = true)]"
			/>
		</ui-pad>
		<ui-spacer />
		<main-health-pricing
			product-name="primecare"
			title="Premium healthcare plans <br /> for your staff"
			description="Choose a plan that best suits your staff and business needs"
			@more-benefit="showBenefits = true"
		/>
		<ui-spacer />
		<ui-pad :ui="{ paddingBlock: '' }">
			<main-most-used-hospitals :count="1" :list="mostlyUsedHospitals" class="mb-10 sm:mb-20" />
			<lazy-molecules-hospital-list
				group-key="primecare"
				:btn-class="{ wrapper: 'max-w-[384px] mx-auto' }"
				class="hidden md:block"
			/>
			<lazy-main-benefit-list-display
				product-category="health"
				product-name="primecare"
				title="Get the best health care coverage for your business."
				description="Find the right plan to fit your company’s health and lifestyle needs."
				class="pt-44"
			/>
			<molecules-process-steps
				:processes="onboardingProcesses"
				class="mt-44"
				title="Simple. Fast. Affordable."
				description="Onboard your team in 3 easy steps!"
				:ui="{ strokeText: { strokeColor: '#A6D7F2', color: 'text-health-100' }, header: { width: '' } }"
				@trigger="() => (isDialogOpen = true)"
			>
				<template #btn>
					<ui-btn
						:ui="{
							btn: {
								base: 'bg-health-500 mt-8 min-h-[56px]',
								onHoverPseudo: 'hover:before:bg-health-800'
							},
							text: {
								base: 'text-white'
							}
						}"
						@click="navigateToProduct('primecare')"
					>
						<span>Get covered</span>
						<template #icon>
							<tools-icon dir="fill" icon="heart-rate-pulse" />
						</template>
					</ui-btn>
				</template>
			</molecules-process-steps>
		</ui-pad>
		<ui-spacer />
		<molecules-important-guides />
		<ui-spacer />
		<ui-pad :ui="{ paddingBlock: '' }">
			<molecules-testimonials />
		</ui-pad>
		<ui-spacer />
		<molecules-demo-card
			title="Comprehensive coverage for your business."
			description=""
			type="health"
			@secondary-click="viewMoreProducts('health')"
		/>
		<ui-spacer />
		<ui-pad :ui="{ paddingBlock: '' }">
			<molecules-investors class="pt-12 pb-20" />
			<main-faq :faq-list="faqList" />
		</ui-pad>
		<ui-spacer />
		<lazy-molecules-benefit-dialog
			v-model:show="showBenefits"
			v-model:focus-on="focusMode"
			product-name="primecare"
			product-category="health"
		/>

		<molecules-auth-dialog v-model="isDialogOpen" selected="register" />
	</div>
</template>
